import { z } from "zod";
import {
  LocalizedTextSchema,
  QuestionRefSchema,
  QuestionResponseTypeSchema,
  RequirementSchema,
  VisibilitySchema,
} from "./common";

export const DependencyRefSchema = z.object({
  question_id: z.number(),
});

export type DependencyRef = z.infer<typeof DependencyRefSchema>;

export const QuestionGroupSchema = z.object({
  _type: z.literal("question_group"),
  id: z.number(),
  name: z.string().optional(),
  title: LocalizedTextSchema.optional(),
  description: LocalizedTextSchema.optional(),
  questions: z.array(QuestionRefSchema),
  dependencies: z.array(DependencyRefSchema).optional(),
  updated_at: z.string(),
  edited_by: z.string().nullable().optional(),
});

export type QuestionGroup = z.infer<typeof QuestionGroupSchema>;

export const PartSchema = z.discriminatedUnion("_type", [
  z
    .object({
      _type: z.literal("question"),
    })
    .merge(QuestionRefSchema),
  z.object({
    _type: z.literal("question_group"),
    visibility: VisibilitySchema,
    question_group_id: z.number(),
  }),
]);

export type Part = z.infer<typeof PartSchema>;

export const PageSchema = z.object({
  title: LocalizedTextSchema.optional(),
  parts: z.array(PartSchema),
});

export type Page = z.infer<typeof PageSchema>;

export const QuestionnaireSchema = z.object({
  _type: z.literal("questionnaire"),
  id: z.number(),
  pages: z.array(PageSchema),
  name: z.string().optional(),
  updated_at: z.string(),
  edited_by: z.string().nullable().optional(),
});

export type Questionnaire = z.infer<typeof QuestionnaireSchema>;

export const validateQuestionGroup = (data: unknown) => {
  return QuestionGroupSchema.safeParse(data);
};

export const validateQuestionnaire = (data: unknown) => {
  return QuestionnaireSchema.safeParse(data);
};

export const ExpandedQuestionSchema = z.object({
  id: z.number(),
  feel_id: z.string(),
  semantic_key: z.string().optional().nullable(),
  question: LocalizedTextSchema.optional().nullable(),
  description: LocalizedTextSchema.optional().nullable(),
  response_type: QuestionResponseTypeSchema.optional().nullable(),
});

export type ExpandedQuestion = z.infer<typeof ExpandedQuestionSchema>;

export const ExpandedQuestionRefSchema = z.object({
  question: ExpandedQuestionSchema.nullable(),
  visibility: VisibilitySchema,
  requirement: RequirementSchema,
});

export type ExpandedQuestionRef = z.infer<typeof ExpandedQuestionRefSchema>;

export const ExpandedDependencySchema = ExpandedQuestionSchema;

export type ExpandedDependency = ExpandedQuestion;

export const ExpandedQuestionGroupSchema = z.object({
  _type: z.literal("question_group").optional(),
  id: z.number(),
  name: z.string().optional().nullable(),
  title: LocalizedTextSchema.optional().nullable(),
  description: LocalizedTextSchema.optional().nullable(),
  questions: z.array(ExpandedQuestionRefSchema),
  dependencies: z.array(ExpandedDependencySchema).optional(),
  updated_at: z.string().optional().nullable(),
  edited_by: z.string().nullable().optional(),
});

export type ExpandedQuestionGroup = z.infer<typeof ExpandedQuestionGroupSchema>;

export const ExpandedPartSchema = z.object({
  _type: z.enum(["question", "question_group"]),
  question: ExpandedQuestionSchema.nullable().optional(),
  question_group: ExpandedQuestionGroupSchema.nullable().optional(),
  visibility: VisibilitySchema,
  requirement: RequirementSchema.optional(),
});

export type ExpandedPart = z.infer<typeof ExpandedPartSchema>;

export const ExpandedPageSchema = z.object({
  title: LocalizedTextSchema.optional(),
  parts: z.array(ExpandedPartSchema),
});

export type ExpandedPage = z.infer<typeof ExpandedPageSchema>;

export const ExpandedQuestionnaireSchema = z.object({
  _type: z.literal("questionnaire").optional(),
  id: z.number(),
  name: z.string().optional(),
  pages: z.array(ExpandedPageSchema),
  updated_at: z.string().optional(),
  edited_by: z.string().nullable().optional(),
});

export type ExpandedQuestionnaire = z.infer<typeof ExpandedQuestionnaireSchema>;

export const expandedQuestionGroupToPersisted = (expanded: ExpandedQuestionGroup): QuestionGroup => ({
  _type: "question_group",
  id: expanded.id,
  name: expanded.name ?? undefined,
  title: expanded.title ?? undefined,
  description: expanded.description ?? undefined,
  questions: expanded.questions.map(qRef => {
    if (!qRef.question) {
      throw new Error(`Question group ${expanded.id} contains a question ref with no inlined question`);
    }
    return {
      question_id: qRef.question.id,
      visibility: qRef.visibility,
      requirement: qRef.requirement,
    };
  }),
  dependencies: expanded.dependencies?.map(dep => ({ question_id: dep.id })),
  updated_at: expanded.updated_at ?? "",
  edited_by: expanded.edited_by,
});

export const expandedQuestionnaireToPersisted = (expanded: ExpandedQuestionnaire): Questionnaire => ({
  _type: "questionnaire",
  id: expanded.id,
  name: expanded.name,
  pages: expanded.pages.map(page => ({
    title: page.title,
    parts: page.parts.map((part): Part => {
      if (part._type === "question") {
        if (!part.question) {
          throw new Error(`Questionnaire ${expanded.id} contains a question part with no inlined question`);
        }
        return {
          _type: "question",
          question_id: part.question.id,
          visibility: part.visibility,
          requirement: part.requirement ?? "optional",
        };
      }
      if (!part.question_group) {
        throw new Error(`Questionnaire ${expanded.id} contains a group part with no inlined question group`);
      }
      return {
        _type: "question_group",
        question_group_id: part.question_group.id,
        visibility: part.visibility,
      };
    }),
  })),
  updated_at: expanded.updated_at ?? "",
  edited_by: expanded.edited_by,
});
